import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { compact } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import LocationSelector from '../LocationSelector'
import Spinner from '../Spinner'
import SimpleTable from '../table/SimpleTable'
import { ImportWizardContext, ImportFile } from 'app/components/ImportWizard'
import type { LocationJson } from 'app/types/entities'
import type { WorkspaceJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { debug, logWarn } from 'lib/logging'
import { OptionType } from 'types/OptionType'

type WorkspaceError = { rowNumber: number; error: string; field: `workspace` | `location` }

export default function Step3() {
  const {
    columnMapping,
    dataToImport,
    locationWorkspaceMapping,
    openErrorModal,
    setErrors,
    setDisableNext,
    setLocationWorkspaceMapping,
    setOpenErrorModal,
  } = useContext(ImportWizardContext)
  const user = useUser()
  const userWorkspace = useWorkspace()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [workspaces, setWorkspaces] = useState<WorkspaceJson[]>()
  const [allWorkspaceOptions, setAllWorkspaceOptions] = useState<OptionType[]>([])
  const [workspaceError, setWorkspaceError] = useState<WorkspaceError[]>([])
  const [queryWorkspaces, setQueryWorkspaces] = useState<string>()

  useEffect(() => {
    let importError: WorkspaceError[] = []
    let columnMap = columnMapping.reduce((acc, column) => ({ ...acc, [column.columnName]: column?.mapping }), {})
    let mapping = dataToImport.reduce((acc: { [key: string]: string }[], item: ImportFile) => {
      return acc.concat({
        sheetLocation: item[columnMap.location],
        mappedLocation: item[columnMap.location],
        sheetWorkspace: item[columnMap.workspace],
        mappedWorkspace: item[columnMap.workspace],
      })
    }, [])
    if (locationWorkspaceMapping.length) {
      setWorkspaces(() =>
        locationWorkspaceMapping
          .filter((mapping) => mapping.workspaceId)
          .reduce((acc: WorkspaceJson[], item) => {
            if (
              item.workspaceId === undefined ||
              acc.find((workspace: WorkspaceJson) => workspace.id === item.workspaceId)
            )
              return acc
            return [...acc, { id: item.workspaceId, name: item.workspaceName }]
          }, []),
      )
      importError = locationWorkspaceMapping.reduce((acc: WorkspaceError[], mapping, index) => {
        if (!mapping.workspaceId) {
          return [
            ...acc,
            {
              rowNumber: index,
              error: mapping?.mappedWorkspace,
              field: `workspace`,
            },
          ]
        } else if (mapping.sheetWorkspace !== mapping?.mappedWorkspace && !mapping.locationId) {
          return [
            ...acc,
            {
              rowNumber: index,
              error: mapping?.mappeLocation,
              field: `location`,
            },
          ]
        }
        return acc
      }, [])
      setWorkspaceError(importError)
      setDisableNext(false)
      setIsLoading(false)
    } else {
      let fileWorkspaces = dataToImport
        .reduce((acc: string[], item: ImportFile) => {
          return acc.includes(`"${item.workspace}"`) ? acc : [...acc, `"${item.workspace}"`]
        }, [])
        .join(` OR `)
      setQueryWorkspaces(fileWorkspaces)
      setLocationWorkspaceMapping(mapping)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    let importError: WorkspaceError[] = []
    let mapping = [...locationWorkspaceMapping]
    if (!queryWorkspaces || !locationWorkspaceMapping.length) return
    bg(async () => {
      setDisableNext(true)
      let result: { hits?: WorkspaceJson[]; error?: string }
      try {
        const [matchResult, allResult] = await Promise.all([
          gqlFetchList<WorkspaceJson>('workspaces', { search: `name:(${queryWorkspaces})` }, 'id name'),
          gqlFetchList<WorkspaceJson>('workspaces', undefined, 'id name'),
        ])
        result = { hits: matchResult.hits }
        setAllWorkspaceOptions(allResult.hits.map((ws) => ({ id: ws.id!, name: ws.name!, value: ws.id! })))
      } catch (error) {
        debug(`import`, `error fetching workspaces`, error)
        logWarn(`Error fetching workspaces`, error)
        notifyError(`Error fetching workspaces, please contact support`)
        setWorkspaces([])
        setDisableNext(false)
        setErrors(1)
        return
      }
      if (result.error) {
        debug(`import`, `error fetching workspaces`, result.error)
        logWarn(`Error fetching workspaces`, result.error)
        notifyError(`Error fetching workspaces, please contact support`)
        setWorkspaces([])
        setDisableNext(false)
        setErrors(1)
        return
      }
      setWorkspaces(result.hits)

      // Resolve workspaces and collect workspaceIds that need location validation
      const workspaceIdsToValidate: Set<string> = new Set()
      locationWorkspaceMapping.forEach((workspaceMapping, index) => {
        let workspace =
          userWorkspace || result.hits?.find((workspace) => workspace.name === workspaceMapping.sheetWorkspace)
        if (workspace) {
          let currentValue = mapping[index]
          currentValue = {
            ...currentValue,
            mappedWorkspace: workspace.name!,
            workspaceName: workspace.name!,
            workspaceId: workspace.id!,
          }
          mapping[index] = currentValue
          workspaceIdsToValidate.add(workspace.id!)
        } else {
          importError.push({
            rowNumber: index,
            error: workspaceMapping.sheetWorkspace,
            field: `workspace`,
          })
        }
      })

      // Fetch locations for all resolved workspaces and validate CSV locations
      if (workspaceIdsToValidate.size > 0) {
        const workspaceIdSearch = [...workspaceIdsToValidate].map((id) => `"${id}"`).join(` OR `)
        try {
          const locationResult = await gqlFetchList<LocationJson>(
            'locations',
            { search: `workspaceId:(${workspaceIdSearch})`, pagination: { limit: 1000 } },
            'id name workspaceId',
          )
          mapping.forEach((row, index) => {
            if (!row.workspaceId) return
            const matchedLocation = locationResult.hits.find(
              (loc) => loc.name === row.sheetLocation && loc.workspaceId === row.workspaceId,
            )
            if (matchedLocation) {
              mapping[index] = { ...row, locationId: matchedLocation.id! }
            } else {
              importError.push({
                rowNumber: index,
                error: row.sheetLocation,
                field: `location`,
              })
            }
          })
        } catch (error) {
          debug(`import`, `error fetching locations for validation`, error)
          logWarn(`Error fetching locations`, error)
        }
      }

      setLocationWorkspaceMapping([...mapping])
      setWorkspaceError(importError)
      setErrors(importError.length)
      setDisableNext(importError.length > 0)
    })
  }, [queryWorkspaces])

  useEffect(() => {
    setErrors(workspaceError.length)
    setDisableNext(false)
  }, [workspaceError])

  let handleLocationChange = (option: LocationJson, rowIndex: number) => {
    if (option.id === `0`) return
    let mapping: { [key: string]: string }[] = [...locationWorkspaceMapping]
    let currentValue = mapping[rowIndex]
    currentValue = {
      ...currentValue,
      mappedLocation: option.name ?? ``,
      locationId: option.id!,
      locationName: option.name!,
    }
    mapping[rowIndex] = currentValue
    setLocationWorkspaceMapping([...mapping])
    setWorkspaceError((prev) => prev.filter((item) => item.rowNumber !== rowIndex))
  }

  let handleWorkspaceChange = (option: WorkspaceJson, rowIndex: number) => {
    if (option.id === `0`) return
    let mapping: { [key: string]: string }[] = [...locationWorkspaceMapping]
    let currentValue = mapping[rowIndex]
    currentValue = {
      ...currentValue,
      mappedWorkspace: option.name ?? ``,
      workspaceName: option.name ?? ``,
      workspaceId: option.id!,
      locationId: ``,
    }
    mapping[rowIndex] = currentValue
    setLocationWorkspaceMapping([...mapping])
    setWorkspaces((prev) => {
      if (prev?.find((workspace) => workspace.name === option.name)) return prev
      return [...(prev ?? []), option]
    })
    // Validate location against new workspace
    bg(async () => {
      try {
        const locationResult = await gqlFetchList<LocationJson>(
          'locations',
          { search: `workspaceId:("${option.id}")`, pagination: { limit: 1000 } },
          'id name workspaceId',
        )
        const matchedLocation = locationResult.hits.find((loc) => loc.name === currentValue.sheetLocation)
        if (matchedLocation) {
          setLocationWorkspaceMapping((prev) => {
            let updated = [...prev]
            updated[rowIndex] = { ...updated[rowIndex], locationId: matchedLocation.id! }
            return updated
          })
          setWorkspaceError((prev) => prev.filter((item) => item.rowNumber !== rowIndex))
        } else {
          setWorkspaceError((prev) => {
            let updated = prev.filter((item) => item.rowNumber !== rowIndex)
            updated.push({
              rowNumber: rowIndex,
              error: currentValue.sheetLocation,
              field: `location`,
            })
            return updated
          })
        }
      } catch (error) {
        debug(`import`, `error validating location for workspace`, error)
        logWarn(`Error validating location`, error)
      }
    })
  }

  let locationMappingColumns = useMemo(
    () =>
      compact([
        {
          id: `sheetLocation`,
          header: `Sheet Location`,
          accessorFn: (row) => row.sheetLocation,
          meta: {
            defaultVisible: true,
          },
        },
        !user?.workspaceId && {
          id: `mappedWorkspace`,
          header: `Sheet Workspace`,
          accessorFn: (row) => row?.mappedWorkspace,
          meta: {
            tooltipClassName: `overflow-visible`,
            defaultVisible: true,
            ControlledComponent: (row, col, index) => {
              const matchedWorkspace = workspaces?.find((workspace) => workspace.name === row?.mappedWorkspace)
              if (workspaces === undefined) {
                return (
                  <div className="flex justify-center">
                    <Spinner size={4} />
                  </div>
                )
              }
              const selectedOption = matchedWorkspace
                ? { id: matchedWorkspace.id!, name: matchedWorkspace.name!, value: matchedWorkspace.id! }
                : { id: ``, name: row.sheetWorkspace, value: `` }
              const hasError = !matchedWorkspace
              return (
                <div className={classNames({ '[&_[data-testid=combo-value]]:text-red-600': hasError })}>
                  <Combo
                    inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-xs"
                    options={allWorkspaceOptions}
                    value={selectedOption}
                    showSearchInDropdown
                    onChange={(option) =>
                      handleWorkspaceChange({ id: option.id, name: option.name } as WorkspaceJson, index)
                    }
                  />
                </div>
              )
            },
          },
        },
        {
          id: `mappedLocation`,
          header: `Location Name`,
          accessorFn: (row) => row?.mappedLocation,
          meta: {
            tooltipClassName: `overflow-visible`,
            defaultVisible: true,
            ControlledComponent: (row, col, index) => {
              const rowWorkspace = workspaces?.find((workspace) => workspace.name === row?.mappedWorkspace)
              const hasWorkspace = !!(rowWorkspace || row.workspaceId || user?.workspaceId)
              if (workspaces === undefined) {
                return (
                  <div className="flex justify-center">
                    <Spinner size={4} />
                  </div>
                )
              }
              const hasLocationError =
                (!user?.workspaceId && !rowWorkspace) || workspaceError.find((item) => item.rowNumber === index)
              return (
                <div className={classNames({ '[&_[data-testid=combo-value]]:text-red-600': hasLocationError })}>
                  <LocationSelector
                    inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-xs"
                    disabled={!hasWorkspace}
                    placeholder={hasWorkspace ? `Search for locations` : `Select a workspace first`}
                    location={
                      hasWorkspace
                        ? ({
                            id: row?.mappedLocation,
                            name: row?.mappedLocation,
                            value: row?.mappedLocation,
                          } as LocationJson)
                        : undefined
                    }
                    filterLocations={{ workspaceId: row.workspaceId ?? rowWorkspace?.id ?? user?.workspaceId }}
                    onChange={(option) => handleLocationChange(option, index)}
                  />
                </div>
              )
            },
          },
        },
      ]),
    [workspaces, workspaceError, locationWorkspaceMapping],
  )

  let errorColums = useMemo(
    (): ColumnDef<WorkspaceError, any>[] => [
      {
        id: `rowNumber`,
        header: `Row number`,
        accessorFn: (row) => row.rowNumber + 1,
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `missed`,
        header: `Message`,
        cell: (props) => {
          if (props.row.original.field === `workspace`) {
            return (
              <span>
                The workspace <strong>{props.row.original.error}</strong> does not currently exist
              </span>
            )
          }
          return (
            <span>
              Please verify that <strong>{props.row.original.error}</strong> is in the correct workspace
            </span>
          )
        },
        meta: {
          defaultVisible: true,
        },
      },
    ],
    [workspaceError],
  )

  const getErrorModalContent = () => (
    <SimpleTable
      columns={errorColums}
      data={workspaceError}
      initialSort={[{ id: `rowNumber`, desc: false }]}
      hideFooter
    />
  )

  useEffect(() => {
    if (openErrorModal)
      showModal(getErrorModalContent(), {
        title: `Errors found`,
        isFullScreen: false,
        is80Percent: true,
        onClose: () => setOpenErrorModal(false),
        useRadix: true,
      })
  }, [openErrorModal])

  if (!user) return null

  return (
    <>
      <h5 className="mb-4">Location Mapping Confirmation. - Edited</h5>
      {isLoading ? (
        <div className="flex justify-center">
          <Spinner size={20} />
        </div>
      ) : (
        <div className="flex h-full w-full grow flex-col overflow-hidden">
          <SimpleTable
            key={locationWorkspaceMapping.length}
            columns={locationMappingColumns}
            data={locationWorkspaceMapping}
            hideFooter
          />
        </div>
      )}
    </>
  )
}
